import { NotificationCategory, NotificationConfig } from '../../types/intelligentScheduling';

export const NOTIFICATION_CONFIGS: Record<string, NotificationConfig> = {
  // Task action notifications - for specific items
  'schedule_reminder': {
    type: 'schedule_reminder',
    category: NotificationCategory.TASK_ACTION,
    defaultActions: [
      { 
        id: 'complete', 
        label: '✅ Complete Item', 
        action: 'complete',
        variant: 'primary'
      },
      { 
        id: 'view', 
        label: '👁️ View List', 
        action: 'navigate',
        data: { target: 'list' }
      },
      { 
        id: 'snooze', 
        label: '⏰ Snooze', 
        action: 'snooze_menu'
      },
      { 
        id: 'skip', 
        label: '⏭️ Skip Item', 
        action: 'dismiss'
      }
    ],
    allowSnooze: true,
    snoozeOptions: [5, 10, 15, 30, 60],
    autoCloseTime: 0
  },

  // List completion notifications
  'list_complete': {
    type: 'list_complete',
    category: NotificationCategory.ACHIEVEMENT,
    defaultActions: [
      { 
        id: 'view_summary', 
        label: '📊 View Summary', 
        action: 'view_summary',
        variant: 'primary'
      },
      { 
        id: 'next_list', 
        label: '➡️ Next List', 
        action: 'navigate_next'
      },
      { 
        id: 'dismiss', 
        label: '✓ Dismiss', 
        action: 'dismiss'
      }
    ],
    allowSnooze: false,
    autoCloseTime: 10000 // Auto-close after 10 seconds
  },

  // Deadline warnings
  'deadline_warning': {
    type: 'deadline_warning',
    category: NotificationCategory.WARNING,
    defaultActions: [
      { 
        id: 'work_now', 
        label: '🚀 Work on it Now', 
        action: 'start_urgent',
        variant: 'danger'
      },
      { 
        id: 'reschedule', 
        label: '📅 Reschedule', 
        action: 'reschedule'
      },
      { 
        id: 'view', 
        label: '👁️ View Details', 
        action: 'navigate',
        data: { target: 'list' }
      },
      { 
        id: 'remind_later', 
        label: '⏰ Remind in 1hr', 
        action: 'snooze',
        data: { minutes: 60 }
      }
    ],
    allowSnooze: true,
    snoozeOptions: [30, 60, 120, 240], // 30min, 1hr, 2hr, 4hr
    autoCloseTime: 0
  },

  // Break reminders
  'break_reminder': {
    type: 'break_reminder',
    category: NotificationCategory.REMINDER,
    defaultActions: [
      { 
        id: 'take_break', 
        label: '☕ Take Break Now', 
        action: 'start_break',
        variant: 'primary'
      },
      { 
        id: 'snooze_5', 
        label: '⏰ In 5 minutes', 
        action: 'snooze',
        data: { minutes: 5 }
      },
      { 
        id: 'snooze_15', 
        label: '⏰ In 15 minutes', 
        action: 'snooze',
        data: { minutes: 15 }
      },
      { 
        id: 'skip', 
        label: '⏭️ Skip Break', 
        action: 'dismiss'
      }
    ],
    allowSnooze: true,
    snoozeOptions: [5, 15, 30],
    autoCloseTime: 5000
  },

  // Task suggestions - for general schedule suggestions
  'task_suggestion': {
    type: 'task_suggestion',
    category: NotificationCategory.REMINDER,
    defaultActions: [
      { 
        id: 'start', 
        label: '▶️ Start Now', 
        action: 'start_task',
        variant: 'primary'
      },
      { 
        id: 'view', 
        label: '👁️ View Task', 
        action: 'navigate',
        data: { target: 'list' }
      },
      { 
        id: 'not_now', 
        label: '⏰ Not Now', 
        action: 'snooze',
        data: { minutes: 30 }
      },
      { 
        id: 'dismiss', 
        label: '❌ Dismiss', 
        action: 'dismiss'
      }
    ],
    allowSnooze: true,
    snoozeOptions: [15, 30, 60, 120],
    autoCloseTime: 8000
  },

  // Item complete notification
  'item_complete': {
    type: 'item_complete',
    category: NotificationCategory.ACHIEVEMENT,
    defaultActions: [
      { 
        id: 'next_item', 
        label: '➡️ Next Item', 
        action: 'show_next_item',
        variant: 'primary'
      },
      { 
        id: 'take_break', 
        label: '☕ Take a Break', 
        action: 'start_break'
      },
      { 
        id: 'dismiss', 
        label: '✓ Done', 
        action: 'dismiss'
      }
    ],
    allowSnooze: false,
    autoCloseTime: 5000
  },

  // Daily summary
  'daily_summary': {
    type: 'daily_summary',
    category: NotificationCategory.INFORMATION,
    defaultActions: [
      { 
        id: 'view_summary', 
        label: '📊 View Full Summary', 
        action: 'navigate',
        data: { target: 'dashboard' },
        variant: 'primary'
      },
      { 
        id: 'plan_tomorrow', 
        label: '📅 Plan Tomorrow', 
        action: 'navigate',
        data: { target: 'calendar' }
      },
      { 
        id: 'dismiss', 
        label: '✓ OK', 
        action: 'dismiss'
      }
    ],
    allowSnooze: false,
    autoCloseTime: 15000
  }
};

// Helper function to get config for a notification type
export function getNotificationConfig(type: string): NotificationConfig {
  return NOTIFICATION_CONFIGS[type] || NOTIFICATION_CONFIGS['schedule_reminder'];
}

// Helper function to get appropriate snooze options for a type
export function getSnoozeOptions(type: string): number[] {
  const config = getNotificationConfig(type);
  return config.snoozeOptions || [5, 10, 15, 30];
}

// Helper function to check if notification type allows snoozing
export function canSnooze(type: string): boolean {
  const config = getNotificationConfig(type);
  return config.allowSnooze;
}